package com.arena_web.ui.components.overlay

import androidx.compose.runtime.*
import com.arena_web.data.dto.CreateUserPostCommentRequest
import com.arena_web.data.dto.UserPostCommentDto
import com.arena_web.data.dto.UserPostDto
import com.arena_web.domain.repositories.UserPostRepository
import com.arena_web.ui.components.cards.PlayerCard
import com.arena_web.ui.components.cards.PlayerCardSize
import com.arena_web.ui.components.feed.PostImageCollage
import com.arena_web.ui.components.icons.Icon
import com.arena_web.ui.components.widgets.HiddenScrollbarStyle
import com.arena_web.ui.localization.LocalAppStrings
import com.arena_web.ui.theme.*
import com.arena_web.ui.utils.*
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.css.Overflow
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.silk.components.graphics.Image
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.toModifier
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.attributes.placeholder
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Input

// Figma 8539:404523 — outer surface (var(--l-s2, #09142d)) and section gradient mirror the
// Player Hub inner-card to keep visual continuity with the profile screen.
private const val MODAL_BG = "var(--l-s2, #09142d)"
private const val SECTION_BG =
    "linear-gradient(173deg, rgba(60, 121, 241, 0.135) 14%, rgba(5, 4, 12, 0.03) 69%)"
private const val DIVIDER = "1px solid rgba(255, 255, 255, 0.05)"
private const val SEND_BG = "#0061FF"
// Heart filter matches the red used elsewhere when a post is liked.
private const val LIKED_HEART_FILTER =
    "brightness(0) saturate(100%) invert(27%) sepia(95%) saturate(4997%) hue-rotate(344deg) brightness(95%) contrast(99%)"

/**
 * Comments dialog opened from a post's chat icon. Mirrors Figma 8539:404523:
 * left column shows the post preview + the post's like/comment counts; right
 * column is a scrollable comment list with a message-input bar pinned to the
 * bottom.
 */
@Composable
fun PostCommentsModal(
    isOpen: Boolean,
    post: UserPostDto?,
    repository: UserPostRepository?,
    onDismiss: () -> Unit,
    onCommentsChanged: (newCount: Int) -> Unit = {},
) {
    if (!isOpen || post == null || repository == null) return

    val scope = rememberCoroutineScope()
    var comments by remember(post.id) { mutableStateOf<List<UserPostCommentDto>>(emptyList()) }
    var totalComments by remember(post.id) { mutableStateOf(post.commentsCount.toLong()) }
    var isLoading by remember(post.id) { mutableStateOf(true) }
    var draft by remember(post.id) { mutableStateOf("") }
    var sending by remember { mutableStateOf(false) }

    // Initial load. Re-loads any time the post id changes so reopening the
    // dialog for a different post starts clean.
    LaunchedEffect(post.id) {
        isLoading = true
        repository.getComments(post.id, mapOf("page" to "1", "size" to "50")).onSuccess { page ->
            comments = page.items
            totalComments = page.totalItems
            onCommentsChanged(page.totalItems.toInt())
        }
        isLoading = false
    }

    val submit = {
        val text = draft.trim()
        if (text.isNotEmpty() && !sending) {
            sending = true
            scope.launch {
                repository.createComment(post.id, CreateUserPostCommentRequest(text)).onSuccess {
                    draft = ""
                    repository.getComments(post.id, mapOf("page" to "1", "size" to "50"))
                        .onSuccess { page ->
                            comments = page.items
                            totalComments = page.totalItems
                            onCommentsChanged(page.totalItems.toInt())
                        }
                }
                sending = false
            }
        }
    }

    val toggleLike: (UserPostCommentDto) -> Unit = onLike@{ comment ->
        val wasLiked = comment.isLikedByMe
        val optimistic = comment.copy(
            isLikedByMe = !wasLiked,
            likesCount = (comment.likesCount + if (wasLiked) -1 else 1).coerceAtLeast(0),
        )
        comments = comments.map { if (it.id == comment.id) optimistic else it }
        scope.launch {
            repository.likeComment(comment.id).onFailure {
                comments = comments.map { if (it.id == comment.id) comment else it }
            }
        }
    }

    // Backdrop + centered card. Stop propagation on the card so the close-on-
    // overlay-click behaviour only fires for the surrounding dim.
    Box(
        modifier = Modifier
            .position(Position.Fixed)
            .top(0.px).left(0.px).right(0.px).bottom(0.px)
            .zIndex(1100)
            .backgroundColor(Color("rgba(0, 0, 0, 0.6)"))
            .onClick { onDismiss() },
        contentAlignment = Alignment.Center,
    ) {
        // Figma 8539:404523 — outer card is 1393 × 908:
        //   padding 24 + left 641 + gap 24 + right 680 + padding 24 = 1393
        //   padding 24 + image 816 + gap 18 + counters ~26 + padding 24 ≈ 908
        // Fixed dimensions (clamped to viewport) so the dialog stays the same
        // size regardless of which image layout the post uses — short
        // landscape layouts just leave breathing room below the photo.
        Box(
            modifier = Modifier
                .styleModifier {
                    property("width", "min(95vw, ${s(1393)})")
                    property("height", "min(90vh, ${s(908)})")
                    property("background", MODAL_BG)
                    property("border-radius", s(46))
                    property("padding", "${s(20)} ${s(24)} ${s(16)} ${s(24)}")
                    property("box-sizing", "border-box")
                }
                .onClick { it.stopPropagation() }
        ) {
            Row(
                modifier = Modifier
                    .fillMaxSize()
                    .styleModifier {
                        property("gap", s(24))
                        property("align-items", "stretch")
                    }
            ) {
                // ── LEFT: post preview + counters ─────────────────────────
                Column(
                    modifier = Modifier.fillMaxHeight().styleModifier {
                        // grow ratio 641 vs the right column's 680 so the split
                        // matches the Figma when the modal scales.
                        property("flex", "641 1 0")
                        property("min-width", "0")
                        property("gap", s(18))
                        // Center the (image + counters) stack vertically so
                        // the counters sit right under the layout and the
                        // empty space is split above & below evenly.
                        property("justify-content", "center")
                    }
                ) {
                    PostPreview(post)
                    PostFooterCounters(
                        likesCount = post.likesCount,
                        isLikedByMe = post.isLikedByMe,
                        commentsCount = totalComments.toInt(),
                    )
                }

                // ── RIGHT: comments + input ───────────────────────────────
                Column(
                    modifier = Modifier.fillMaxHeight()
                        .styleModifier {
                            property("flex", "680 1 0")
                            property("min-width", "0")
                            property("background", SECTION_BG)
                            property("border-radius", s(22))
                            property("padding", "0 ${s(24)} ${s(18)} ${s(24)}")
                            property("overflow", "hidden")
                            property("box-sizing", "border-box")
                        }
                ) {
                    // Scrollable comments area — even when empty we keep the
                    // flexGrow so the input bar stays anchored at the bottom
                    // and the panel itself doesn't collapse. Per request: no
                    // empty-state text, just blank space.
                    Column(
                        modifier = HiddenScrollbarStyle.toModifier()
                            .fillMaxWidth()
                            .flexGrow(1)
                            .minHeight(0.px)
                            .styleModifier { property("overflow-y", "auto") }
                    ) {
                        comments.forEachIndexed { index, c ->
                            CommentRow(
                                comment = c,
                                onLikeClick = { toggleLike(c) },
                                showDivider = index != comments.lastIndex,
                            )
                        }
                    }
                    // Horizontal divider above the input — Figma "Section
                    // Underline". Painted with ButtonBorderGradientPrimary so
                    // it matches the rest of the dialog's chrome.
                    Box(
                        modifier = Modifier.fillMaxWidth().sHeight(1).styleModifier {
                            property("background", AppGradients.ButtonBorderGradientPrimary)
                        }
                    )
                    MessageInputBar(
                        value = draft,
                        onValueChange = { draft = it },
                        onSubmit = { submit() },
                        sending = sending,
                    )
                }
            }
        }
    }
}

@Composable
private fun PostPreview(post: UserPostDto) {
    // No flex-grow — the wrapper sizes itself to the collage's natural
    // height so the counters sit immediately below it. The default
    // flex-shrink: 1 + min-height: 0 lets the column clip overly-tall
    // layouts instead of pushing the counters off-screen.
    Box(
        modifier = Modifier
            .fillMaxWidth()
            .styleModifier {
                property("min-height", "0")
                property("border-radius", s(22))
                property("overflow", "hidden")
                if (post.images.isEmpty()) {
                    property("background", "var(--arena-glass)")
                    // Empty placeholder needs a real size to be visible.
                    property("aspect-ratio", "4 / 3")
                }
            }
    ) {
        if (post.images.isNotEmpty()) {
            // singleFullWidth=true: single-image layouts (1, 2) fill the
            // dialog's left column instead of the 75% width used inline.
            PostImageCollage(
                urls = post.images.map { it.imageUrl },
                layout = post.layout,
                singleFullWidth = true,
            )
        }
    }
}

@Composable
private fun PostFooterCounters(likesCount: Int, isLikedByMe: Boolean, commentsCount: Int) {
    Row(
        modifier = Modifier.sGap(16),
        verticalAlignment = Alignment.CenterVertically,
    ) {
        Row(modifier = Modifier.sGap(8), verticalAlignment = Alignment.CenterVertically) {
            Image(
                src = AppIcons.Heart,
                modifier = Modifier.sSize(24).styleModifier {
                    if (isLikedByMe) property("filter", LIKED_HEART_FILTER)
                }
            )
            CounterText(likesCount.toString())
        }
        Row(modifier = Modifier.sGap(8), verticalAlignment = Alignment.CenterVertically) {
            Icon(src = AppIcons.Chat, modifier = Modifier.sSize(21))
            CounterText(commentsCount.toString())
        }
    }
}

@Composable
private fun CounterText(text: String) {
    SpanText(
        text,
        modifier = Modifier
            .sFontSize(18)
            .fontWeight(AppTypography.Regular)
            .sLineHeight(18)
            .sLetterSpacing(0.5)
            .color(com.arena_web.ui.theme.ThemeVars.Text2)
    )
}

@Composable
private fun CommentRow(
    comment: UserPostCommentDto,
    onLikeClick: () -> Unit,
    showDivider: Boolean,
) {
    val t = LocalAppStrings.current
    Column(
        modifier = Modifier
            .fillMaxWidth()
            .styleModifier {
                property("padding", "${s(20)} 0")
                if (showDivider) property("border-bottom", DIVIDER)
            }
    ) {
        Row(
            modifier = Modifier.sGap(14),
            verticalAlignment = Alignment.Top,
        ) {
            // 40×60 player card avatar — matches the small "Highlight" card used
            // in FeedPostCard so the visual language stays consistent.
            Box(modifier = Modifier.styleModifier { property("flex-shrink", "0") }) {
                if (comment.authorCardImageUrl != null) {
                    PlayerCard(
                        playerName = "",
                        rating = 0,
                        position = "",
                        sport = "football",
                        size = PlayerCardSize.Highlight,
                        cardFrameUrl = comment.authorCardImageUrl,
                        cardAssetsBasePath = comment.authorCardAssetsBasePath,
                        cardTextColor = comment.authorCardTextColor,
                        avatarUrl = comment.authorCardAvatarUrl ?: AppIcons.EmptyAvatar,
                        hideRating = true,
                        hideShadow = true,
                    )
                } else {
                    Box(
                        modifier = Modifier
                            .sSize(40)
                            .sBorderRadius(100)
                            .styleModifier {
                                property("overflow", "hidden")
                                property("background-color", "#1A2238")
                                if (comment.authorAvatarUrl != null) {
                                    property("background-image", "url('${comment.authorAvatarUrl}')")
                                    property("background-size", "cover")
                                    property("background-position", "center")
                                }
                            }
                    )
                }
            }
            Column(
                modifier = Modifier.flexGrow(1).minWidth(0.px).sGap(6)
            ) {
                Row(
                    modifier = Modifier.sGap(6),
                    verticalAlignment = Alignment.CenterVertically,
                ) {
                    SpanText(
                        comment.authorName,
                        modifier = Modifier
                            .sFontSize(18)
                            .fontWeight(AppTypography.Medium)
                            .sLineHeight(22)
                            .sLetterSpacing(0.09)
                            .color(com.arena_web.ui.theme.ThemeVars.Text1)
                    )
                    Box(
                        modifier = Modifier
                            .sSize(3)
                            .sBorderRadius(100)
                            .backgroundColor(AppColors.Text.Muted)
                    )
                    SpanText(
                        formatRelativeTime(comment.createdAt),
                        modifier = Modifier
                            .sFontSize(14)
                            .fontWeight(AppTypography.Light)
                            .sLineHeight(18)
                            .sLetterSpacing(0.028)
                            .color(com.arena_web.ui.theme.ThemeVars.Text3)
                    )
                }
                SpanText(
                    comment.content,
                    modifier = Modifier
                        .fillMaxWidth()
                        .sFontSize(16)
                        .fontWeight(AppTypography.Regular)
                        .sLineHeight(24)
                        .sLetterSpacing(0.16)
                        .color(com.arena_web.ui.theme.ThemeVars.TextMuted)
                )
                Row(
                    modifier = Modifier.sGap(6).cursor(Cursor.Pointer).onClick { onLikeClick() },
                    verticalAlignment = Alignment.CenterVertically,
                ) {
                    Image(
                        src = AppIcons.Heart,
                        modifier = Modifier.sSize(14).styleModifier {
                            if (comment.isLikedByMe) property("filter", LIKED_HEART_FILTER)
                        }
                    )
                    SpanText(
                        when (comment.likesCount) {
                            0 -> t.postCommentsLike
                            1 -> t.postCommentsOneLike
                            else -> t.postCommentsLikes(comment.likesCount)
                        },
                        modifier = Modifier
                            .sFontSize(14)
                            .fontWeight(AppTypography.Light)
                            .sLineHeight(18)
                            .sLetterSpacing(0.028)
                            .color(com.arena_web.ui.theme.ThemeVars.TextMuted)
                    )
                }
            }
        }
    }
}

@Composable
private fun MessageInputBar(
    value: String,
    onValueChange: (String) -> Unit,
    onSubmit: () -> Unit,
    sending: Boolean,
) {
    val t = LocalAppStrings.current
    // Outer Box owns positioning so the gradient-border overlay can sit
    // absolutely inside it (chat "+" button pattern from the design system).
    Box(
        modifier = Modifier
            .fillMaxWidth()
            .sHeight(48)
            .styleModifier {
                property("position", "relative")
                property("border-radius", s(28))
                property("margin-top", s(12))
            }
    ) {
        // ButtonBorderGradientPrimary as a mask-composite overlay — keeps the
        // input's interior untouched while painting only the rounded border.
        Box(
            modifier = Modifier.fillMaxSize().styleModifier {
                property("position", "absolute")
                property("inset", "0")
                property("border", "0.3px solid transparent")
                property("border-radius", s(28))
                property("background", "${AppGradients.ButtonBorderGradientPrimary} border-box")
                property("-webkit-mask", "linear-gradient(#fff 0 0) padding-box, linear-gradient(#fff 0 0)")
                property("mask", "linear-gradient(#fff 0 0) padding-box, linear-gradient(#fff 0 0)")
                property("-webkit-mask-composite", "xor")
                property("mask-composite", "exclude")
                property("pointer-events", "none")
            }
        )
        Row(
            modifier = Modifier
                .fillMaxSize()
                .styleModifier { property("padding", "0 ${s(12)}") },
            verticalAlignment = Alignment.CenterVertically,
        ) {
            Input(
                type = org.jetbrains.compose.web.attributes.InputType.Text,
                attrs = {
                    value(value)
                    onInput { onValueChange(it.value) }
                    placeholder(t.postCommentsInputPlaceholder)
                    onKeyDown { e ->
                        if (e.key == "Enter" && !e.shiftKey) {
                            e.preventDefault()
                            onSubmit()
                        }
                    }
                    style {
                        property("flex", "1 1 0")
                        property("min-width", "0")
                        property("background", "transparent")
                        property("border", "none")
                        property("outline", "none")
                        property("color", "var(--arena-text-2)")
                        property("font-size", s(16))
                        property("font-family", "Urbanist, sans-serif")
                    }
                }
            )
            Box(
                modifier = Modifier
                    .sSize(32)
                    .sBorderRadius(100)
                    .backgroundColor(Color(SEND_BG))
                    .cursor(if (sending || value.isBlank()) Cursor.NotAllowed else Cursor.Pointer)
                    .styleModifier { if (sending || value.isBlank()) property("opacity", "0.5") }
                    .onClick { onSubmit() },
                contentAlignment = Alignment.Center,
            ) {
                Icon(src = AppIcons.Send, modifier = Modifier.sSize(16))
            }
        }
    }
}

/** "2 min ago" / "1 hour ago" / "1 day ago" / dated fallback. */
private fun formatRelativeTime(isoDate: String): String = try {
    val date = kotlin.js.Date(isoDate)
    val now = kotlin.js.Date()
    val diffMs = now.getTime() - date.getTime()
    val diffMin = (diffMs / 60000).toLong()
    val diffHours = (diffMs / 3600000).toLong()
    val diffDays = (diffMs / 86400000).toLong()
    when {
        diffMin < 1 -> "just now"
        diffMin < 60 -> "$diffMin min ago"
        diffHours < 24 -> "$diffHours hour${if (diffHours == 1L) "" else "s"} ago"
        diffDays < 7 -> "$diffDays day${if (diffDays == 1L) "" else "s"} ago"
        else -> {
            val day = date.getDate()
            val month = date.toLocaleString("en", js("({month:'short'})")).toString()
            "$day $month"
        }
    }
} catch (_: Throwable) {
    isoDate
}
